import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as ApiService from "@/services/ApiService";

const FACILITY_KEY = import.meta.env.VITE_APP_FACILITY_KEY ?? null;

/** Facility key: selected patient first (from GetUserPatients), then link (magic link), store, env (build-time), or facilityID fallback for legacy. */
export function getFacilityKey(): string {
  const state = useProfileStore.getState();
  const fromPatient = state.currentPatient;
  if (fromPatient?.facilityKey && String(fromPatient.facilityKey).trim())
    return String(fromPatient.facilityKey);
  const fromLink = state.linkInfo?.facilityKey;
  if (fromLink && String(fromLink).trim()) return String(fromLink);
  const fromStore = state.facilityKey;
  if (fromStore && String(fromStore).trim()) return String(fromStore);
  const fromEnv = import.meta.env.VITE_APP_FACILITY_KEY;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv);
  const fromPatientId = state.currentPatient?.facilityID;
  return fromPatientId != null ? String(fromPatientId) : "";
}

/** Facility key for Portal API only. Prefer selected patient (GetUserPatients), then link, store, env. Never returns facilityID. */
export function getFacilityKeyForPortal(): string {
  const state = useProfileStore.getState();
  const fromPatient = state.currentPatient;
  if (fromPatient?.facilityKey && String(fromPatient.facilityKey).trim())
    return String(fromPatient.facilityKey);
  const fromLink = state.linkInfo?.facilityKey;
  if (fromLink && String(fromLink).trim()) return String(fromLink);
  const fromStore = state.facilityKey;
  if (fromStore && String(fromStore).trim()) return String(fromStore);
  const fromEnv = import.meta.env.VITE_APP_FACILITY_KEY;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv);
  return "";
}

/** Patient key for Portal API. From selected patient (GetUserPatients), then link, else patientID as string. */
export function getPatientKey(): string {
  const state = useProfileStore.getState();
  const fromLink = state.linkInfo?.patientKey;
  if (fromLink && String(fromLink).trim()) return String(fromLink);
  const p = state.currentPatient;
  if (p?.patientKey && String(p.patientKey).trim()) return String(p.patientKey);
  return p?.patientID != null ? String(p.patientID) : "";
}

export interface FacilityInfo {
  officeName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  openEdge: boolean;
  fortis: boolean;
}

export interface LinkInfo {
  expired: boolean;
  facilityID: number;
  facilityKey: string;
  patientID: number;
  patientKey: string;
  taskID: number;
  taskType: string;
  userExists: boolean;
}

export interface AuthInfo {
  firstName: string;
  lastName: string;
  nickName: string;
  email: string;
  [key: string]: unknown;
}

export interface Patient {
  id: number;
  entryDate: string;
  patientPortalUserID: number;
  facilityID: number;
  patientID: number;
  /** From GetUserPatients; use for Portal API (facilityKey/patientKey). */
  facilityKey?: string;
  /** From GetUserPatients; use for Portal API. */
  patientKey?: string;
  firstName: string;
  lastName: string;
  authRep: boolean;
  officeName: string;
  /** From API / patient lookup when available; used e.g. for health history (female-specific questions). */
  sex?: string;
  /** From API / patient lookup when available; used e.g. for health history year ranges. */
  dob?: string;
}

interface ProfileState {
  facilityKey: string | null;
  intakeKey: string | null;
  profileAuthInfo: AuthInfo | null;
  patientAssociated: Patient[] | null;
  currentPatient: Patient | null;
  facilityInfo: FacilityInfo | null;
  linkInfo: LinkInfo | null;
  currentProfilePageView: string | null;
  currentProfilePaymentView: string;
  setFacilityKey: (key: string | null) => void;
  setIntakeKey: (key: string | null) => void;
  setProfileAuthInfo: (info: AuthInfo | null) => void;
  setProfileMyInfo: (payload: Partial<AuthInfo>) => void;
  setPatientAssociated: (patients: Patient[] | null) => void;
  setCurrentPatient: (patient: Patient | null) => void;
  /** Merge fields into the selected patient without resetting case history / complaint flow (e.g. after demographics save). */
  patchCurrentPatient: (partial: Partial<Patient>) => void;
  setFacilityInfo: (info: FacilityInfo | null) => void;
  setLinkInfo: (info: LinkInfo | null) => void;
  setProfileCurrentPage: (view: string | null) => void;
  setProfilePaymentCurrentPage: (view: string) => void;
  getLink: (key: string) => Promise<LinkInfo | null>;
  logout: () => void;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      facilityKey: FACILITY_KEY,
      intakeKey: null,
      profileAuthInfo: null,
      patientAssociated: null,
      currentPatient: null,
      facilityInfo: null,
      linkInfo: null,
      currentProfilePageView: null,
      currentProfilePaymentView: "PaymentCardList",
      setFacilityKey: (key) => set({ facilityKey: key }),
      setIntakeKey: (key) => set({ intakeKey: key }),
      setProfileAuthInfo: (info) => set({ profileAuthInfo: info }),
      setProfileMyInfo: (payload) => {
        const current = get().profileAuthInfo;
        if (!current) return;
        set({
          profileAuthInfo: {
            ...current,
            firstName: (payload.firstName ?? current.firstName) as string,
            lastName: (payload.lastName ?? current.lastName) as string,
            nickName: (payload.nickName ?? current.nickName) as string,
            email: (payload.email ?? current.email) as string,
          },
        });
      },
      setPatientAssociated: (patients) => set({ patientAssociated: patients }),
      setCurrentPatient: (patient) => {
        set({ currentPatient: patient });
        void import("./task").then(({ useTaskStore }) => {
          useTaskStore.getState().resetCaseHistory();
        });
        void import("./primaryComplaint").then(({ usePrimaryComplaintStore }) => {
          usePrimaryComplaintStore.getState().startNewComplaint();
        });
      },
      patchCurrentPatient: (partial) => {
        set((s) => {
          if (!s.currentPatient) return {};
          return { currentPatient: { ...s.currentPatient, ...partial } };
        });
      },
      setFacilityInfo: (info) => set({ facilityInfo: info }),
      setLinkInfo: (info) => set({ linkInfo: info }),
      setProfileCurrentPage: (view) => set({ currentProfilePageView: view }),
      setProfilePaymentCurrentPage: (view) =>
        set({ currentProfilePaymentView: view }),
      getLink: async (key: string) => {
        try {
          const response = await ApiService.get(`/PatientPortal/GetLink/${encodeURIComponent(key)}`);
          const data = (response.data as LinkInfo) ?? null;
          set({ linkInfo: data });
          return data;
        } catch (err) {
          console.error("API Error:", err);
          throw err;
        }
      },
      logout: () =>
        set({
          profileAuthInfo: null,
          patientAssociated: null,
          currentPatient: null,
        }),
    }),
    { name: "portal-profile" }
  )
);
